import {
  taskKeyOf,
  taskPriorityLabels,
  taskStatusLabels,
  taskTypeLabels,
  type Task,
  type TaskPriority,
  type TaskStatus,
  type TaskType,
} from '@nexo/types';
import { cn } from '@nexo/ui';
import { Bookmark, Bug, CheckSquare, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';

/** Status as a circle: empty (todo), half (in progress), checked (done). */
export function StatusIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const color =
    status === 'done' ? 'text-success' : status === 'in_progress' ? 'text-accent' : 'text-subtle';
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={cn('size-4 shrink-0', color, className)}>
      {status === 'done' ? (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path
            d="M5 8.2 7 10.2 11 6"
            fill="none"
            stroke="var(--color-surface)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          {status === 'in_progress' && <path d="M8 4a4 4 0 0 1 0 8z" fill="currentColor" />}
        </>
      )}
    </svg>
  );
}

/** Clickable status icon that advances the task to the next status. */
export function StatusButton({
  status,
  onChange,
  size = 'md',
}: {
  status: TaskStatus;
  onChange: (next: TaskStatus) => void;
  size?: 'sm' | 'md';
}) {
  const { t } = useTranslation();
  const next: TaskStatus =
    status === 'todo' ? 'in_progress' : status === 'in_progress' ? 'done' : 'todo';
  const from = t(taskStatusLabels[status]);
  const to = t(taskStatusLabels[next]);
  return (
    <button
      type="button"
      title={`${from} → ${to}`}
      aria-label={t('Estado: {{from}}. Cambiar a {{to}}', { from, to })}
      onClick={(event) => {
        event.stopPropagation();
        onChange(next);
      }}
      className="-m-1 rounded-full p-1 transition-transform hover:scale-110 hover:bg-surface-2 active:scale-95"
    >
      <StatusIcon status={status} className={size === 'sm' ? 'size-3.5' : 'size-[18px]'} />
    </button>
  );
}

/** Signal-bars style priority icon (urgent is a filled alert square). */
export function PriorityIcon({
  priority,
  className,
  showNormal = false,
}: {
  priority: TaskPriority;
  className?: string;
  showNormal?: boolean;
}) {
  const { t } = useTranslation();
  if (priority === 'normal' && !showNormal) return null;
  const label = t('Prioridad {{priority}}', {
    priority: t(taskPriorityLabels[priority], { context: 'priority' }).toLowerCase(),
  });
  if (priority === 'urgent') {
    return (
      <svg
        viewBox="0 0 16 16"
        role="img"
        aria-label={label}
        className={cn('size-4 shrink-0 text-danger', className)}
      >
        <title>{label}</title>
        <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill="currentColor" />
        <path
          d="M8 4.5v4.2M8 11.2v.3"
          stroke="var(--color-surface)"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  const level = priority === 'high' ? 3 : priority === 'normal' ? 2 : 1;
  const color = priority === 'high' ? 'text-warning' : 'text-muted';
  return (
    <svg
      viewBox="0 0 16 16"
      role="img"
      aria-label={label}
      className={cn('size-4 shrink-0', color, className)}
    >
      <title>{label}</title>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={2.5 + i * 4}
          y={10 - i * 3}
          width="3"
          height={4 + i * 3}
          rx="1"
          fill="currentColor"
          opacity={i < level ? 1 : 0.22}
        />
      ))}
    </svg>
  );
}

const typeIcons: Record<TaskType, { icon: typeof Bug; className: string }> = {
  task: { icon: CheckSquare, className: 'bg-accent text-white' },
  story: { icon: Bookmark, className: 'bg-success text-white' },
  bug: { icon: Bug, className: 'bg-danger text-white' },
  epic: { icon: Zap, className: 'bg-violet-500 text-white' },
};

/** Jira-style coloured square for the task type. */
export function TypeIcon({ type, className }: { type: TaskType; className?: string }) {
  const { t } = useTranslation();
  const { icon: Icon, className: tone } = typeIcons[type];
  return (
    <span
      role="img"
      aria-label={t(taskTypeLabels[type], { context: 'taskType' })}
      title={t(taskTypeLabels[type], { context: 'taskType' })}
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center rounded-[4px]',
        tone,
        className,
      )}
    >
      <Icon className="size-2.5" strokeWidth={2.75} aria-hidden />
    </span>
  );
}

/** `NEX-42`, in the team's current key. */
export function TaskKey({ task, className }: { task: Pick<Task, 'number'>; className?: string }) {
  const { team } = useTeam();
  return (
    <span className={cn('shrink-0 font-mono text-[11.5px] text-subtle tabular-nums', className)}>
      {taskKeyOf(team.taskKey, task.number)}
    </span>
  );
}

/** Story points as a small pill (nothing when not estimated). */
export function PointsBadge({ points, className }: { points: number | null; className?: string }) {
  const { t } = useTranslation();
  if (points === null) return null;
  return (
    <span
      title={
        points === 1
          ? t('1 punto de historia')
          : t('{{count}} puntos de historia', { count: points })
      }
      className={cn(
        'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-surface-3 px-1.5 text-[11px] font-semibold text-muted tabular-nums',
        className,
      )}
    >
      {points}
    </span>
  );
}
